import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Users, AlertTriangle, TrendingUp, FileCheck, Zap, UserX, Hourglass, AlarmClock, ArrowRight, Play,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
} from "recharts";
import api from "@/lib/api";
import { fmtMoney, STAGE_COLORS } from "@/lib/constants";
import GuidedDemo from "@/components/GuidedDemo";

const STAGE_BAR_COLORS = {
  "Lead": "#38bdf8", "FTD": "#f59e0b", "KYC": "#a78bfa", "Funded": "#22d3ee",
  "Active Trader": "#10b981", "Retention Risk": "#fb923c", "Dormant": "#71717a", "Withdrawal": "#ef4444",
};

const tooltipStyle = {
  backgroundColor: "#18181b", border: "1px solid #27272a", borderRadius: 4,
  fontSize: 12, fontFamily: "IBM Plex Mono, monospace",
};

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [demoOpen, setDemoOpen] = useState(false);

  useEffect(() => {
    api.get("/dashboard/metrics").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) {
    return <div className="font-mono text-zinc-500 text-sm animate-pulse" data-testid="dashboard-loading">LOADING METRICS…</div>;
  }

  const m = data.metrics;
  const kpis = [
    { label: "Clients Requiring Action", value: m.total_action, icon: Users, accent: "text-zinc-100", testId: "kpi-total-action" },
    { label: "Est. Revenue at Risk", value: fmtMoney(m.revenue_at_risk), icon: AlertTriangle, accent: "text-red-400", glow: true, testId: "kpi-revenue-at-risk" },
    { label: "Recovered Opportunities", value: `${m.recovered_count}`, sub: fmtMoney(m.recovered_value), icon: TrendingUp, accent: "text-emerald-400", glow: true, testId: "kpi-recovered" },
    { label: "KYC Completion Opportunities", value: m.kyc_opps, icon: FileCheck, accent: "text-violet-400", testId: "kpi-kyc-opps" },
    { label: "FTD Conversion Opportunities", value: m.ftd_opps, icon: Zap, accent: "text-amber-400", testId: "kpi-ftd-opps" },
    { label: "Retention-Risk Clients", value: m.retention_risk, icon: UserX, accent: "text-orange-400", testId: "kpi-retention-risk" },
    { label: "Pending Human Approvals", value: m.pending_approvals, icon: Hourglass, accent: "text-sky-400", testId: "kpi-pending-approvals" },
    { label: "Overdue Actions & Escalations", value: m.overdue, icon: AlarmClock, accent: "text-red-400", testId: "kpi-overdue" },
  ];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-100">Operational Dashboard</h1>
          <p className="text-sm text-zinc-500 mt-1">AI-detected revenue opportunities across the client lifecycle — synthetic data.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="guided-demo-button"
            onClick={() => setDemoOpen(true)}
            className="flex items-center gap-2 text-xs font-semibold text-zinc-950 bg-emerald-500 hover:bg-emerald-600 rounded-sm px-3 py-2 transition-colors duration-200 shadow-[0_0_10px_rgba(16,185,129,0.2)] hover:shadow-[0_0_20px_rgba(16,185,129,0.4)]"
          >
            <Play className="w-3.5 h-3.5" strokeWidth={1.5} /> Guided Demo
          </button>
          <Link
            to="/queue"
            data-testid="view-full-queue-link"
            className="flex items-center gap-2 text-xs font-semibold text-emerald-400 hover:text-emerald-300 border border-emerald-500/40 hover:bg-emerald-500/10 rounded-sm px-3 py-2 transition-colors duration-200"
          >
            Full client queue <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
          </Link>
        </div>
      </div>
      <GuidedDemo open={demoOpen} onOpenChange={setDemoOpen} />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((k, i) => (
          <div
            key={k.label}
            data-testid={k.testId}
            style={{ animationDelay: `${i * 40}ms` }}
            className={`fade-slide-in bg-zinc-900 border border-zinc-800 rounded-md p-5 hover:border-zinc-700 transition-colors duration-200 ${k.glow ? "shadow-[0_0_15px_rgba(16,185,129,0.08)]" : ""}`}
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">{k.label}</span>
              <k.icon className={`w-4 h-4 ${k.accent}`} strokeWidth={1.5} />
            </div>
            <div className={`text-3xl font-bold font-mono tracking-tight ${k.accent}`}>{k.value}</div>
            {k.sub && <div className="text-xs font-mono text-zinc-500 mt-1">{k.sub} recovered value</div>}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-zinc-900 border border-zinc-800 rounded-md p-6" data-testid="chart-stage-distribution">
          <h3 className="text-base font-semibold text-zinc-200 mb-1">Clients by Lifecycle Stage</h3>
          <p className="text-xs text-zinc-500 mb-4">Live distribution across all 8 stages</p>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.stage_distribution} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
              <XAxis dataKey="stage" tick={{ fill: "#71717a", fontSize: 10 }} angle={-25} textAnchor="end" height={50} interval={0} />
              <YAxis tick={{ fill: "#71717a", fontSize: 11 }} allowDecimals={false} />
              <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "#27272a" }} />
              <Bar dataKey="count" radius={[2, 2, 0, 0]}>
                {data.stage_distribution.map((d) => (
                  <Cell key={d.stage} fill={STAGE_BAR_COLORS[d.stage] || "#10b981"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-zinc-900 border border-zinc-800 rounded-md p-6" data-testid="chart-revenue-by-stage">
          <h3 className="text-base font-semibold text-zinc-200 mb-1">Open Commercial Value by Stage</h3>
          <p className="text-xs text-zinc-500 mb-4">Unresolved estimated value (EUR)</p>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.revenue_by_stage} margin={{ top: 0, right: 0, left: -10, bottom: 0 }}>
              <XAxis dataKey="stage" tick={{ fill: "#71717a", fontSize: 10 }} angle={-25} textAnchor="end" height={50} interval={0} />
              <YAxis tick={{ fill: "#71717a", fontSize: 11 }} />
              <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "#27272a" }} formatter={(v) => fmtMoney(v)} />
              <Bar dataKey="value" fill="#10b981" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-md" data-testid="dashboard-priority-preview">
        <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between">
          <h3 className="text-base font-semibold text-zinc-200">Highest-Priority Clients</h3>
          <span className="text-[10px] font-mono text-zinc-500 tracking-widest uppercase">Top 5 by AI priority score</span>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-800 text-left">
              <th className="px-6 py-3 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Client</th>
              <th className="px-4 py-3 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Stage</th>
              <th className="px-4 py-3 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Stuck At</th>
              <th className="px-4 py-3 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider text-right">Score</th>
              <th className="px-6 py-3 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider text-right">Est. Value</th>
            </tr>
          </thead>
          <tbody>
            {data.priority_queue.map((c) => (
              <tr key={c.id} className="border-b border-zinc-800/60 hover:bg-zinc-800/50 transition-colors duration-150">
                <td className="px-6 py-3">
                  <Link to={`/clients/${c.id}`} data-testid={`dashboard-client-link-${c.id}`} className="font-semibold text-zinc-100 hover:text-emerald-400 transition-colors duration-150">
                    {c.name}
                  </Link>
                  <div className="text-[11px] font-mono text-zinc-500">{c.id}</div>
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${STAGE_COLORS[c.stage] || ""}`}>{c.stage}</span>
                </td>
                <td className="px-4 py-3 text-xs text-zinc-400 max-w-xs truncate">{c.stuck_at}</td>
                <td className="px-4 py-3 text-right font-mono text-emerald-400 font-semibold">{c.score}</td>
                <td className="px-6 py-3 text-right font-mono text-zinc-200">{fmtMoney(c.value)}</td>
              </tr>
            ))}
            {data.priority_queue.length === 0 && (
              <tr><td colSpan={5} className="px-6 py-8 text-center text-sm text-zinc-500">All clients resolved. Reset the demo to replay.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
